import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { commitMutation, createFragmentContainer, graphql } from 'react-relay';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { useToast } from 'components/Toasts/ToastProvider';
import moment, { Moment } from 'moment';
import { PendingBackgroundCheckTab_delegate } from './__generated__/PendingBackgroundCheckTab_delegate.graphql';
import RaisedButton from 'components/Button/RaisedButton';
import SectionCard from 'features/background-checks/components/SectionCard';
import CheckStatus from 'features/background-checks/components/CheckStatus';
import {
  DBSExpiryModal,
  IDExpiryModal,
  ExpiryKind
} from 'features/background-checks/components/ExpiryDateModal';
import InterviewNotesModal from 'features/background-checks/components/InterviewNotesModal';
import ReferencesModal from 'features/background-checks/components/ReferencesModal';
import Timeline from 'views/components/Timeline';
import {
  getStatus,
  statusText
} from 'features/background-checks/utils/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faClipboardCheck,
  faShieldCheck,
  faIdBadge,
  faUserShield,
  faUserCheck,
  faRotateRight,
  faEnvelope,
  faEnvelopeOpen,
  faClock
} from '@fortawesome/pro-duotone-svg-icons';
import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons';
import { faArrowDown } from '@fortawesome/pro-regular-svg-icons';

const GREY = '#6A728F';
const AMBER = '#f68f1f';
const GREEN = '#1FD426';

const useStyles = createUseStyles((theme: Theme) => ({
  overviewCard: {
    background: 'white',
    border: '1px solid #E0E3E7',
    borderRadius: 8,
    padding: 25,
    marginBottom: 20
  },
  overviewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  overviewTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    margin: 0
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: 20
  },
  '@keyframes hintIn': {
    from: { opacity: 0, transform: 'translate(-50%, 8px)' },
    to: { opacity: 1, transform: 'translate(-50%, 0)' }
  },
  actionsHint: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '9px 18px',
    background: '#0b57ff',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow:
      '0 4px 14px rgba(11, 87, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
    borderRadius: 999,
    fontSize: 12.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    animation: '$hintIn 0.25s ease-out',
    transition: 'opacity 0.3s ease, transform 0.3s ease'
  },
  actionsHintOut: {
    opacity: 0,
    transform: 'translate(-50%, 8px)',
    pointerEvents: 'none'
  },
  headerRequested: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  headerRequested__label: {
    color: GREY,
    margin: 0,
    fontSize: '0.8rem'
  },
  headerRequested__text: {
    margin: 0,
    fontSize: '0.9rem'
  },
  timelineDivider: {
    height: 1,
    background: '#ededed',
    margin: '20px 0'
  },
  timelineTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: GREY,
    margin: '0 0 12px 0'
  },
  timelineEmpty: {
    padding: '26px 20px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    textAlign: 'center',
    color: GREY,
    fontSize: '0.8rem'
  },
  typePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: '#3A3F58',
    whiteSpace: 'nowrap'
  },
  typePillIcon: {
    color: GREY
  },
  checkSelect: {
    marginLeft: 'auto',
    appearance: 'none',
    '-webkit-appearance': 'none',
    '-moz-appearance': 'none',
    backgroundColor: 'white',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236A728F' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    border: '1px solid #E0E3E7',
    borderRadius: 6,
    padding: '6px 34px 6px 10px',
    fontSize: 13,
    fontWeight: 500,
    color: '#3A3F58',
    cursor: 'pointer',
    outline: 'none'
  },
  statusText: {
    color: GREY,
    fontSize: '0.8rem',
    margin: 0
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px'
  },
  emptyCard: {
    background: 'white',
    border: '1px solid #E0E3E7',
    borderRadius: 8,
    padding: 25
  },
  emptyPanel: {
    padding: '46px 20px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px'
  },
  emptyIcon: {
    fontSize: 28,
    color: GREY
  },
  emptyText: {
    color: GREY,
    fontSize: '0.8rem',
    margin: 0
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    margin: '0 0 12px 0'
  }
}));

const updateIDExpiryMutation = graphql`
  mutation PendingBackgroundCheckTab_UpdateIDExpiryMutation(
    $backgroundCheckUUID: UUID!
    $date: Time!
  ) {
    updateIDExpiryDate(backgroundCheckUUID: $backgroundCheckUUID, date: $date)
  }
`;

const updateDBSExpiryMutation = graphql`
  mutation PendingBackgroundCheckTab_UpdateDBSExpiryMutation(
    $backgroundCheckUUID: UUID!
    $date: Time!
  ) {
    updateDBSExpiryDate(backgroundCheckUUID: $backgroundCheckUUID, date: $date)
  }
`;

type Props = {
  delegate: PendingBackgroundCheckTab_delegate;
  onUpdated?: () => void;
  // Deep-link support (e.g. from the outstanding items email)
  initialCheckUUID?: string;
  initialModal?: 'dbs' | 'id' | 'notes';
};

const PendingBackgroundCheckTab = ({
  delegate,
  onUpdated,
  initialCheckUUID,
  initialModal
}: Props) => {
  const classes = useStyles();
  const toast = useToast();

  // Pending = not yet approved. Verifications are uploaded existing checks,
  // not something in progress, so they don't count as pending here
  const pendingChecks = (delegate.backgroundChecks ?? []).filter(
    (c) => !c.approvalDate && !c.isVerification
  );

  const [selectedUUID, setSelectedUUID] = React.useState<string | null>(
    initialCheckUUID ?? null
  );
  const [expiryModal, setExpiryModal] = React.useState<ExpiryKind | null>(
    initialModal === 'dbs' || initialModal === 'id' ? initialModal : null
  );
  const [notesModalOpen, setNotesModalOpen] = React.useState(
    initialModal === 'notes'
  );
  const [referencesOpen, setReferencesOpen] = React.useState(false);

  // "Actions required below" hint — dismissed for good once the bottom of
  // the tab has been seen. Dismissal fades the pill out (hintDismissed),
  // then unmounts it (hintGone); if the bottom is already visible at mount
  // it skips straight to gone so nothing flashes
  const [hintDismissed, setHintDismissed] = React.useState(false);
  const [hintGone, setHintGone] = React.useState(false);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const hintMountedAt = React.useRef(Date.now());

  React.useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        if (Date.now() - hintMountedAt.current < 300) {
          setHintGone(true);
        }
        setHintDismissed(true);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!hintDismissed || hintGone) return;
    const timeout = setTimeout(() => setHintGone(true), 350);
    return () => clearTimeout(timeout);
  }, [hintDismissed, hintGone]);

  const check =
    pendingChecks.find((c) => c.uuid === selectedUUID) ??
    pendingChecks[0] ??
    null;

  const saveExpiryDate = (kind: ExpiryKind, expiryDate: Moment) => {
    if (!check) return;

    const label = kind === 'dbs' ? 'DBS' : 'ID';

    commitMutation(environment, {
      mutation:
        kind === 'dbs' ? updateDBSExpiryMutation : updateIDExpiryMutation,
      variables: {
        backgroundCheckUUID: check.uuid,
        date: expiryDate
      },
      onCompleted: (_response, payloadErrors) => {
        const errors = (payloadErrors as unknown) as GraphError[] | null;
        if (errors && errors.length > 0) {
          toast({
            message: {
              variant: 'error',
              text: errors?.[0]?.extensions?.helpText ?? errors?.[0]?.message
            }
          });
          console.error(errors);
          return;
        }

        toast({
          message: {
            variant: 'success',
            text: `${label} expiry date saved`
          }
        });
        setExpiryModal(null);
        onUpdated && onUpdated();
      },
      onError: (err: any) => {
        console.error(err);
        toast({
          message: {
            variant: 'error',
            text:
              err?.[0]?.extensions?.helpText ?? err?.[0]?.message ?? err.message
          }
        });
      }
    });
  };

  if (!check) {
    return (
      <div className={classes.emptyCard}>
        <p className={classes.subtitle}>Pending background check</p>
        <div className={classes.emptyPanel}>
          <FontAwesomeIcon
            icon={faShieldHalved}
            className={classes.emptyIcon}
          />
          <p className={classes.emptyText}>
            {delegate.hasActiveBackgroundCheck
              ? 'No pending checks - this delegate has an active background check'
              : 'No pending background check for this delegate'}
          </p>
        </div>
      </div>
    );
  }

  const status = getStatus(check);

  const typeLabelFor = (c: typeof check) =>
    c.isRenewal
      ? 'Renewal'
      : c.isVerification
      ? 'Verification'
      : c.onlyDBS
      ? 'DBS Check'
      : 'Initial';

  const checkTypeLabel = typeLabelFor(check);

  const checkTypeIcon = check.isRenewal
    ? faRotateRight
    : check.isVerification
    ? faClipboardCheck
    : check.onlyDBS
    ? faIdBadge
    : faShieldCheck;

  const expiryColor = (date: string) =>
    moment(date).diff(moment(), 'days') > 60 ? GREEN : AMBER;

  const formatDate = (date: string) => moment(date).format('DD/MM/YYYY');

  // Initial checks show everything; DBS-only and renewals just ID + DBS
  const isInitial = !check.isRenewal && !check.onlyDBS;

  const showApplication = isInitial;
  const applicationCard = check.applicationReceivedAt
    ? {
        icon: faEnvelopeOpen,
        iconColor: GREEN,
        text: `Application received ${formatDate(check.applicationReceivedAt)}`,
        subText: check.applicationSentAt
          ? `Sent ${formatDate(check.applicationSentAt)}`
          : undefined
      }
    : check.applicationSentAt
    ? {
        icon: faEnvelope,
        iconColor: AMBER,
        text: 'Waiting on the delegate to complete their application',
        subText: `Sent ${formatDate(check.applicationSentAt)}`
      }
    : {
        icon: faEnvelope,
        iconColor: GREY,
        text: 'Application not sent yet'
      };

  const showReferences = isInitial;
  const referencesCard = check.hasAllResponses
    ? {
        icon: faClipboardCheck,
        iconColor: GREEN,
        text: 'All responses received'
      }
    : check.applicationReceivedAt
    ? {
        icon: faClock,
        iconColor: AMBER,
        text: 'Waiting on references to respond'
      }
    : {
        icon: faClock,
        iconColor: GREY,
        text: 'No references yet - waiting on the application'
      };

  const idCard = check.idExpiryDate
    ? {
        icon: faUserCheck,
        iconColor: expiryColor(check.idExpiryDate),
        text: `ID expires ${formatDate(check.idExpiryDate)}`
      }
    : {
        icon: faUserCheck,
        iconColor: AMBER,
        text: 'ID expiry date needed'
      };

  const dbsCard = check.dbsExpiryDate
    ? {
        icon: faIdBadge,
        iconColor: expiryColor(check.dbsExpiryDate),
        text: `DBS expires ${formatDate(check.dbsExpiryDate)}`
      }
    : {
        icon: faUserShield,
        iconColor: AMBER,
        text: 'DBS expiry date needed'
      };

  const showInterviewNotes = isInitial && check.isCAA;
  const interviewNotesCard = check.interviewNotes
    ? {
        icon: faClipboardList,
        iconColor: GREEN,
        text: 'Interview notes received'
      }
    : {
        icon: faClipboardList,
        iconColor: AMBER,
        text: 'Interview notes required'
      };

  const activeHistory = check.history?.filter((item) => !item.isArchived) ?? [];

  // The actions a manager can take right now, mirrored as quick-reference
  // buttons in the overview and driving the "actions required" hint
  const needsIDExpiry = !check.idExpiryDate;
  const needsDBSExpiry = !check.dbsExpiryDate;
  const needsInterviewNotes = showInterviewNotes && !check.interviewNotes;
  const hasActions = needsIDExpiry || needsDBSExpiry || needsInterviewNotes;

  return (
    <div>
      <div className={classes.overviewCard}>
        <div className={classes.overviewHeader}>
          <p className={classes.overviewTitle}>Overview</p>
          {pendingChecks.length === 1 && (
            <div className={classes.typePill}>
              <FontAwesomeIcon
                icon={checkTypeIcon}
                className={classes.typePillIcon}
              />
              {checkTypeLabel}
            </div>
          )}
          {check.isCAA && (
            <div className={classes.typePill}>
              <FontAwesomeIcon
                icon={faUserShield}
                className={classes.typePillIcon}
              />
              CAA
            </div>
          )}
          {pendingChecks.length > 1 ? (
            <select
              className={classes.checkSelect}
              value={check.uuid}
              onChange={(e) => setSelectedUUID(e.target.value)}
            >
              {pendingChecks.map((c) => (
                <option key={c.uuid} value={c.uuid}>
                  {typeLabelFor(c)} - requested{' '}
                  {c.createdAt ? formatDate(c.createdAt) : '-'}
                </option>
              ))}
            </select>
          ) : (
            <div className={classes.headerRequested}>
              <p className={classes.headerRequested__label}>Requested</p>
              <p className={classes.headerRequested__text}>
                {check.createdAt ? formatDate(check.createdAt) : '-'}
              </p>
            </div>
          )}
        </div>

        {!check.onlyDBS && (
          <div className={classes.infoRow}>
            <CheckStatus status={status} />
            <p className={classes.statusText}>{statusText[status]}</p>
          </div>
        )}

        {isInitial && (
          <>
            <div className={classes.timelineDivider} />
            <p className={classes.timelineTitle}>Employment history timeline</p>
            {activeHistory.length > 0 && check.applicationReceivedAt ? (
              <Timeline
                history={activeHistory}
                timelineStartDate={moment(check.applicationReceivedAt)}
                yearsBack={check.isCAA ? 5 : 3}
                onSectionClick={() => setReferencesOpen(true)}
              />
            ) : (
              <div className={classes.timelineEmpty}>
                Timeline available once the application has been received
              </div>
            )}
          </>
        )}
      </div>

      <div className={classes.cardGrid}>
        {showApplication && (
          <SectionCard title="Application" {...applicationCard} />
        )}
        {showReferences && (
          <SectionCard
            title="References"
            {...referencesCard}
            action={
              activeHistory.length > 0 && (
                <RaisedButton onClick={() => setReferencesOpen(true)}>
                  <FontAwesomeIcon icon={faClock} /> View references
                </RaisedButton>
              )
            }
          />
        )}
        <SectionCard
          title="ID expiry"
          {...idCard}
          action={
            needsIDExpiry && (
              <RaisedButton primary onClick={() => setExpiryModal('id')}>
                <FontAwesomeIcon icon={faUserCheck} /> Add expiry date
              </RaisedButton>
            )
          }
        />
        <SectionCard
          title="DBS expiry"
          {...dbsCard}
          action={
            needsDBSExpiry && (
              <RaisedButton primary onClick={() => setExpiryModal('dbs')}>
                <FontAwesomeIcon icon={faIdBadge} /> Add expiry date
              </RaisedButton>
            )
          }
        />
        {showInterviewNotes && (
          <SectionCard
            title="Interview notes"
            {...interviewNotesCard}
            action={
              !check.interviewNotes && (
                <RaisedButton primary onClick={() => setNotesModalOpen(true)}>
                  <FontAwesomeIcon icon={faClipboardList} /> Add interview notes
                </RaisedButton>
              )
            }
          />
        )}
      </div>

      <IDExpiryModal
        isOpen={expiryModal === 'id' && needsIDExpiry}
        onClose={() => setExpiryModal(null)}
        onSubmit={(expiryDate) => saveExpiryDate('id', expiryDate)}
      />
      <DBSExpiryModal
        isOpen={expiryModal === 'dbs' && needsDBSExpiry}
        onClose={() => setExpiryModal(null)}
        onSubmit={(expiryDate) => saveExpiryDate('dbs', expiryDate)}
      />
      {notesModalOpen && needsInterviewNotes && (
        <InterviewNotesModal
          backgroundCheckUUID={check.uuid}
          onClose={() => setNotesModalOpen(false)}
          onSubmitted={onUpdated}
        />
      )}
      <ReferencesModal
        isOpen={referencesOpen}
        onClose={() => setReferencesOpen(false)}
        history={activeHistory}
      />

      <div ref={bottomRef} />
      {hasActions && !hintGone && (
        <div
          className={`${classes.actionsHint} ${
            hintDismissed ? classes.actionsHintOut : ''
          }`}
          onClick={() =>
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
        >
          <FontAwesomeIcon icon={faArrowDown} /> Actions required below
        </div>
      )}
    </div>
  );
};

const PendingBackgroundCheckTabFrag = createFragmentContainer(
  PendingBackgroundCheckTab,
  {
    delegate: graphql`
      fragment PendingBackgroundCheckTab_delegate on Delegate {
        hasActiveBackgroundCheck
        backgroundChecks {
          uuid
          createdAt
          approvalDate
          applicationSentAt
          applicationReceivedAt
          hasAllResponses
          requestedDBS
          isRenewal
          isVerification
          isCAA
          onlyDBS
          idExpiryDate
          dbsExpiryDate
          interviewNotes {
            yourName
          }
          history {
            ...Timeline_history
            uuid
            isArchived
            responseSendLogs {
              emailAddress
              sentAt
            }
            employer {
              uuid
              application {
                uuid
                name
                contactEmail
                jobTitle
                dates {
                  startDate
                  endDate
                }
              }
              response {
                uuid
                orgName
                orgContactName
                datesEmployed {
                  startDate
                  endDate
                }
              }
            }
            education {
              uuid
              application {
                uuid
                name
                contactEmail
                dates {
                  startDate
                  endDate
                }
              }
              response {
                uuid
                orgName
                orgContactName
                dates {
                  startDate
                  endDate
                }
              }
            }
            selfEmployed {
              uuid
              application {
                uuid
                accountantName
                contactEmail
                dates {
                  startDate
                  endDate
                }
              }
              response {
                uuid
                orgName
                orgContactName
                dates {
                  startDate
                  endDate
                }
              }
            }
            unemployed {
              uuid
              application {
                uuid
                dates {
                  startDate
                  endDate
                }
              }
              response {
                uuid
                alternateDates {
                  startDate
                  endDate
                }
              }
            }
          }
        }
      }
    `
  }
);

export default PendingBackgroundCheckTabFrag;
